import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, SortAsc, SortDesc } from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [movies, setMovies] = useState([]);
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedAgeRating, setSelectedAgeRating] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [genres, setGenres] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [years, setYears] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMovies();
  }, []);

  // Debounced search history recording
  useEffect(() => {
    if (!searchQuery || !searchQuery.trim()) return;
    const timer = setTimeout(() => {
      axios.post(`${API}/search-history`, { query: searchQuery }, { withCredentials: true }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchMovies = async () => {
    try {
      const { data } = await axios.get(`${API}/movies`, { withCredentials: true });
      setMovies(data);
      
      const uniqueGenres = [...new Set(data.flatMap(m => m.genre || []))].sort();
      const uniqueLanguages = [...new Set(data.map(m => m.language).filter(Boolean))].sort();
      const uniqueYears = [...new Set(data.map(m => m.release_year).filter(Boolean))].sort((a, b) => b - a);
      setGenres(uniqueGenres);
      setLanguages(uniqueLanguages);
      setYears(uniqueYears);
    } catch (error) {
      console.error('Error fetching movies:', error);
    }
  };

  const filterAndSortMovies = useCallback(() => {
    let filtered = movies;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(movie =>
        movie.title?.toLowerCase().includes(query) ||
        movie.director?.toLowerCase().includes(query) ||
        movie.cast?.some(actor => actor.toLowerCase().includes(query)) ||
        movie.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (selectedGenre) {
      filtered = filtered.filter(movie => movie.genre?.includes(selectedGenre));
    }

    if (selectedLanguage) {
      filtered = filtered.filter(movie => movie.language === selectedLanguage);
    }

    if (selectedAgeRating) {
      filtered = filtered.filter(movie => movie.age_rating === selectedAgeRating);
    }

    if (selectedYear) {
      filtered = filtered.filter(movie => movie.release_year === parseInt(selectedYear));
    }

    // Sort
    filtered = [...filtered];
    switch (sortBy) {
      case 'name_asc':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'name_desc':
        filtered.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'year_desc':
        filtered.sort((a, b) => b.release_year - a.release_year);
        break;
      case 'year_asc':
        filtered.sort((a, b) => a.release_year - b.release_year);
        break;
      case 'rating_desc':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
    }

    setFilteredMovies(filtered);
  }, [searchQuery, selectedGenre, selectedLanguage, selectedAgeRating, selectedYear, sortBy, movies]);

  useEffect(() => {
    filterAndSortMovies();
  }, [filterAndSortMovies]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGenre('');
    setSelectedLanguage('');
    setSelectedAgeRating('');
    setSelectedYear('');
    setSortBy('newest');
  };

  const selectStyle = {
    backgroundColor: '#1A2632',
    border: '1px solid #2A3F52',
    color: '#FFFFFF'
  };

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="search-page">
      <h1 className="text-4xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Search & Discover</h1>

      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, actor, director, or tags..."
            className="w-full px-6 py-4 pl-14 rounded-lg outline-none text-lg"
            style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52', color: '#FFFFFF' }}
            data-testid="search-input"
          />
          <Search size={24} className="absolute left-4 top-1/2 transform -translate-y-1/2" style={{ color: '#8197A4' }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none min-w-[180px]"
            style={selectStyle}
            data-testid="sort-by-filter"
          >
            <option value="newest">Newest First</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
            <option value="year_desc">Year (Newest)</option>
            <option value="year_asc">Year (Oldest)</option>
            <option value="rating_desc">Rating (High to Low)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Genre</label>
          <select
            value={selectedGenre}
            onChange={(e) => setSelectedGenre(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={selectStyle}
            data-testid="genre-filter"
          >
            <option value="">All Genres</option>
            {genres.map(genre => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Language</label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={selectStyle}
            data-testid="language-filter"
          >
            <option value="">All Languages</option>
            {languages.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={selectStyle}
            data-testid="year-filter"
          >
            <option value="">All Years</option>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#8197A4' }}>Age Rating</label>
          <select
            value={selectedAgeRating}
            onChange={(e) => setSelectedAgeRating(e.target.value)}
            className="px-4 py-2 rounded-lg outline-none"
            style={selectStyle}
            data-testid="age-rating-filter"
          >
            <option value="">All Ratings</option>
            <option value="G">G</option>
            <option value="PG">PG</option>
            <option value="PG13">PG13</option>
            <option value="NC16">NC16</option>
            <option value="M18">M18</option>
            <option value="R21">R21</option>
          </select>
        </div>

        <button
          onClick={clearFilters}
          className="px-4 py-2 rounded-lg font-medium transition-colors duration-200"
          style={{ backgroundColor: '#2A3F52', color: '#FFFFFF', border: '1px solid #3E5468' }}
          data-testid="clear-filters-button"
        >
          Clear Filters
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <p style={{ color: '#8197A4' }}>{filteredMovies.length} movie{filteredMovies.length !== 1 ? 's' : ''} found</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {filteredMovies.map((movie) => (
          <div
            key={movie.movie_id}
            className="movie-card-hover cursor-pointer rounded-lg overflow-hidden"
            style={{ backgroundColor: '#1A2632' }}
            onClick={() => navigate(`/movie/${movie.movie_id}`)}
            data-testid={`search-result-${movie.movie_id}`}
          >
            <div className="relative" style={{ aspectRatio: '2/3' }}>
              <img
                src={mediaUrl(movie.poster_path, API) || `https://picsum.photos/seed/${movie.movie_id}/400/600`}
                alt={movie.title}
                className="w-full h-full object-cover"
              />
              {movie.is_restricted && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}>
                  <p className="text-sm text-center px-4 font-semibold" style={{ color: '#EF4444' }}>Age Restricted</p>
                </div>
              )}
            </div>
            <div className="p-3">
              <h3 className="font-semibold text-sm mb-1 truncate" style={{ color: '#FFFFFF' }}>{movie.title}</h3>
              <div className="flex items-center justify-between text-xs" style={{ color: '#8197A4' }}>
                <span>{movie.release_year}</span>
                <span className="px-2 py-1 rounded" style={{ backgroundColor: '#0F171E' }}>{movie.age_rating}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredMovies.length === 0 && (
        <div className="text-center py-16 rounded-xl" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }}>
          <p className="text-lg" style={{ color: '#FFFFFF' }}>No movies found matching your criteria</p>
          <p className="text-sm mt-2" style={{ color: '#8197A4' }}>Try adjusting your filters or search terms</p>
        </div>
      )}
    </div>
  );
};
